import React, { useState } from 'react'

import { ApolloError } from '@apollo/client'
import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Dialog, DialogContent, DialogActions, Button } from '@mui/material'
import { FormProvider, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import { FplDialogTitle } from 'components/common/Dialog'
import {
  BodyType,
  CreateAndUpdateVehicleDtoInput,
  useCreateVehicleMutation,
  useDeleteVehicleMutation,
  useUpdateVehicleMutation,
} from 'generated/graphql'
import { Restricted } from 'providers'
import { useConfirmDialog } from 'providers/ConfirmDialogProvider'

import { DeleteVehicleDialog } from '../Vehicles/DeleteVehicleDialog'
import UpdateVehicle from './UpdateVehicle'
import { VehicleSchema } from './ValidationSchema'
import { VehicleForm } from './VehicleForm'
import VehicleFormHeader from './VehicleFormHeader'

interface IProps {
  fplDataGridRef: React.MutableRefObject<any>
  driverId: number
  vehicleId: number | null
  openDriverDialog: boolean
  handleCloseDialog: () => void
}

const defaultValues: CreateAndUpdateVehicleDtoInput = {
  driverId: '',
  id: '',
  isDefault: null,
  isRenewalReminder: false,
  registrationNumber: '',
  vehicleTypeId: 1,
  colour: '',
  dateOfManufacture: null,
  goodsInTransitExpiryDate: null,
  insuranceCompany: '',
  insuranceExpiryDate: null,
  manufacturer: '',
  model: '',
  motTestExpiryDate: null,
  policyOrCoverNote: '',
  bodyType: BodyType.Box,
  hasTailLift: false,
  specialServiceIds: [],
}

const CreateUpdateVehicle = (props: IProps) => {
  const { driverId, vehicleId, openDriverDialog, handleCloseDialog } = props

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    vehicleId: 0,
  })

  const [notFound, setNotFound] = React.useState(false)

  const methods = useForm<CreateAndUpdateVehicleDtoInput>({
    shouldUnregister: true,
    defaultValues: defaultValues,
    resolver: joiResolver(VehicleSchema),
  })

  const {
    reset,
    handleSubmit,
    formState: { isDirty },
  } = methods

  const closeAndResetDiadlog = () => {
    handleCloseDialog()
    reset(defaultValues)
  }

  const { confirmOpen: confirmClosingDirtyForm } = useConfirmDialog({
    onConfirmApprove: closeAndResetDiadlog,
  })

  const [createVehicle, { loading: creatingVehicle }] = useCreateVehicleMutation()

  const [updateVehicle, { loading: updatingVehicle }] = useUpdateVehicleMutation()

  const [deleteVehicle, { loading: deletingVehicle }] = useDeleteVehicleMutation({
    onCompleted: (response) => {
      if (response.deleteVehicle) {
        toast.success('Vehicle was successfully deleted.')

        setDeleteDialog({ open: false, vehicleId: 0 })
        closeAndResetDiadlog()
        props.fplDataGridRef.current.queryRefetchOnTableUpdate()
      } else {
        toast.error('Something went wrong, please try again later.')
      }
    },
  })

  const handleSuccess = (data: CreateAndUpdateVehicleDtoInput) => {
    if (!vehicleId) {
      createVehicle({
        variables: {
          input: {
            ...data,
            timeZoneOffset: new Date().getTimezoneOffset(),
            driverId: String(driverId),
            vehicleTypeId: Number(data.vehicleTypeId),
            specialServiceIds: data.specialServiceIds ?? [],
          },
        },
      })
        .then(() => {
          toast.success('Vehicle was successfully created.')

          handleCloseDialog()
          props.fplDataGridRef.current.queryRefetchOnTableUpdate()
        })
        .catch((reason: ApolloError) => reason.graphQLErrors.map((e) => toast.error(e.message)))
    } else if (vehicleId && !notFound) {
      updateVehicle({
        variables: {
          input: {
            ...data,
            timeZoneOffset: new Date().getTimezoneOffset(),
            id: String(vehicleId),
            driverId: String(driverId),
            vehicleTypeId: Number(data.vehicleTypeId),
            specialServiceIds: data.specialServiceIds ?? [],
          },
        },
      })
        .then(() => {
          toast.success('Vehicle was successfully updated.')
          closeAndResetDiadlog()
        })
        .catch((reason: ApolloError) => reason.graphQLErrors.map((e) => toast.error(e.message)))
    }
  }

  const handleDeleteClick = () => {
    if (!vehicleId) {
      return
    }

    setDeleteDialog({
      open: true,
      vehicleId: vehicleId,
    })
  }

  const handleOnDeleteVehicleConfirmed = () =>
    deleteVehicle({
      variables: {
        vehicleId: deleteDialog.vehicleId.toString(),
      },
    })

  const handleOnDialogClose = () => setDeleteDialog({ open: false, vehicleId: 0 })

  const handleCancel = () => {
    if (isDirty) {
      confirmClosingDirtyForm()
    } else {
      closeAndResetDiadlog()
    }
  }

  const isProcessing = creatingVehicle || updatingVehicle

  return (
    <Dialog
      fullWidth
      maxWidth='md'
      open={openDriverDialog}
      closeAfterTransition={false}
      aria-labelledby='form-vehicle-dialog'>
      <FplDialogTitle id='form-vehicle-dialog-title' onClose={handleCancel}>
        <VehicleFormHeader notFound={notFound} vehicleId={vehicleId} />
      </FplDialogTitle>

      {!notFound && (
        <DialogContent>
          <FormProvider {...methods}>
            <form id='create-update-vehicle-form' onSubmit={handleSubmit(handleSuccess)} noValidate>
              {!vehicleId ? (
                <VehicleForm />
              ) : (
                <UpdateVehicle
                  driverId={driverId}
                  vehicleId={vehicleId as number}
                  setNotFound={setNotFound}
                />
              )}
            </form>
          </FormProvider>
        </DialogContent>
      )}

      <DialogActions>
        <Button variant='contained' color='grey' onClick={handleCancel} disabled={isProcessing}>
          Cancel
        </Button>
        <Restricted to='DeleteVehicle'>
          <LoadingButton
            disabled={!vehicleId}
            variant='contained'
            color='error'
            onClick={handleDeleteClick}
            loading={deletingVehicle}>
            Delete
          </LoadingButton>
        </Restricted>
        <Button
          form='create-update-vehicle-form'
          color='primary'
          variant='contained'
          disabled={isProcessing || !isDirty}
          type='submit'>
          Save
        </Button>
      </DialogActions>
      <DeleteVehicleDialog
        open={deleteDialog.open}
        vehicleId={Number(deleteDialog.vehicleId)}
        driverId={driverId}
        onClose={handleOnDialogClose}
        processing={deletingVehicle}
        onConfirm={handleOnDeleteVehicleConfirmed}
      />
    </Dialog>
  )
}

export default CreateUpdateVehicle
